import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useAppStateProvider } from "../../../contextProviders/AppStateProvider";
import createTranslator from "../../../util/translationHelper";
import messages from "../translations/messages";
import helper from "./oauthCallbackHelper";
import pricingHelper from "../Pricing/pricingHelper";
import { OauthCallbackState } from "./constants";
import { MODAL_STATUS } from "../../../components/StatusModal/constants";
import useAccountingFeatureFlag from "../useAccountingFeatureFlag";
import useAccountingGuard from "../useAccountingGuard";
import { clearChangePlanData } from "../qbo_AccountingHelper";
import getToken from "../../../util/tokenGenerator";
import { handleTokenRequest } from "../../../util/helper";

function useOauthCallback(params) {
  const { lang, history } = params;
  const BLOCK = useRef("qbo-oauth-callback");
  // window.name persists across navigations in the same window and is a reliable
  // COOP-resistant identifier. It is the primary signal for popup detection.
  // localStorage is the fallback for browsers that may clear window.name on
  // cross-origin navigation (e.g. Firefox privacy partitioning).
  const isInPopup = useRef(
    window.name === "oauthPopup" || localStorage.getItem("QBO_OAUTH_POPUP") === "true"
  );
  const { appState, updateAppStateLocal } = useAppStateProvider();
  const isChangePlanMode = appState?.changePlanMode === true;

  const { isEnabled: ffPassed } = useAccountingFeatureFlag({ lang, history });
  const { isReady: guardReady } = useAccountingGuard({ lang, history, skip: isChangePlanMode });
  const [callbackState, setCallbackState] = useState(
    OauthCallbackState.LOADING,
  );
  const { instance, inProgress, accounts } = useMsal();
  const tokenhandler = useCallback(
    async () => await handleTokenRequest(() => getToken(instance, inProgress, accounts)),
    [instance, inProgress, accounts]
  );

  const translate = useMemo(() => {
    return createTranslator(messages);
  }, [createTranslator, messages]);

  // If running inside the OAuth popup, relay the callback URL to the parent and close.
  useEffect(() => {
    if (!isInPopup.current) return;
    localStorage.setItem("QBO_OAUTH_CALLBACK_URL", window.location.href);
    window.close();
  }, []);

  const handleButton = useCallback(async () => {
    if (isChangePlanMode) {
      if (callbackState === OauthCallbackState.COMPLETE) {
        clearChangePlanData();
        updateAppStateLocal({
          changePlanMode: false,
          changePlanNewFrequency: null,
          changePlanSuccess: true,
        });
        history.push(`/${lang}/qbo-accounting`);
      } else {
        history.push(`/${lang}/qbo-accounting/pricing`);
      }
      return;
    }

    const redirect_url = (callbackState === OauthCallbackState.COMPLETE) ? `/${lang}/qbo-accounting` : `/${lang}/qbo-accounting/terms`;
    if (callbackState === OauthCallbackState.COMPLETE) {
        localStorage.setItem(
          "QBOMonerisConnectionStatus",
          JSON.stringify({ status: MODAL_STATUS.success, shownAlready: false }),
        );
      }
    if (isInPopup.current) {
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.location.href = redirect_url;
        }
      } catch (e) {
        // Cross-origin or COOP restriction
      }

      localStorage.setItem(
        "QBO_OAUTH_CALLBACK_URL",
        `${window.location.origin}${redirect_url}`,
      );

      window.close();
      return;
    }

    history.push(redirect_url);
  }, [callbackState, lang, history, isChangePlanMode, updateAppStateLocal]);

  const processChangePlan = useCallback(async () => {
    const newFrequency = appState?.changePlanNewFrequency;
    if (!newFrequency) {
      setCallbackState(OauthCallbackState.ERROR);
      return;
    }

    try {
      const response = await pricingHelper.submitChangePlan(newFrequency, tokenhandler);

      if (response?.status === 200 || response?.status === 201) {
        setCallbackState(OauthCallbackState.COMPLETE);
      } else {
        setCallbackState(OauthCallbackState.ERROR);
      }
    } catch {
      setCallbackState(OauthCallbackState.ERROR);
    }
  }, [appState?.changePlanNewFrequency, tokenhandler]);

  const processOAuthCallback = useCallback(async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const realmId = urlParams.get("realmId");
      const code = urlParams.get("code");
      const error = urlParams.get("error");

      if (error) {
        setCallbackState(OauthCallbackState.ERROR);
        return;
      }

      const result = await helper.createAccountingConnection(
        tokenhandler,
        realmId,
        code,
      );

      if (result.success && result.status === 200) {
        setCallbackState(OauthCallbackState.COMPLETE);
      } else {
        setCallbackState(OauthCallbackState.ERROR);
      }
    } catch (error) {
      setCallbackState(OauthCallbackState.ERROR);
    }
  }, [setCallbackState, tokenhandler]);

  useEffect(() => {
    if (callbackState !== OauthCallbackState.LOADING || !ffPassed || !guardReady) return;

    if (isChangePlanMode) {
      processChangePlan();
    } else {
      processOAuthCallback();
    }
  }, [ffPassed, guardReady, callbackState, isChangePlanMode, processChangePlan, processOAuthCallback]);

  return {
    BLOCK,
    translate,
    callbackState,
    handleButton,
    isChangePlanMode,
  };
}

const Hook = {
  useOauthCallback,
};

export default Hook;
